<template>
  <header class="shadow flex items-center dark:bg-gray-900 dark:text-gray-100 py-4 bg-white">
    <div class="px-4 flex items-center">
      <kt-button icon-only icon="ki-duotone ki-burger-menu-6" class="lg:hidden" @click="toggleSidebar" />
    </div>

    <div class="flex-grow"></div>

    <div class="px-4 flex flex-row gap-4 items-center relative">
      <div class="relative inline-block" ref="notifDropdownRef">
        <button
          @click="toggleNotifDropdown"
          class="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 group transition-all duration-200"
        >
          <kt-icon
            name="notification"
            type="duotone"
            class="text-xl text-gray-600 dark:text-gray-300 group-hover:text-primary"
          />
          <span
            v-if="notif_counts > 0"
            class="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full border-2 border-white dark:border-gray-900 animate-pulse"
          >
            {{ notif_counts }}
          </span>
        </button>

        <transition name="fade-slide">
          <kt-card
            v-if="isNotifDropdownOpen"
            class="absolute top-full right-0 mt-3 w-[550px] max-h-[550px] z-50 shadow-2xl border border-gray-100 dark:border-gray-800 rounded-2xl overflow-hidden flex flex-col"
          >
            <!-- Modernized Header with Action Button -->
            <kt-card-header
              class="border-b border-gray-100 dark:border-gray-800 px-4 py-3 flex items-center justify-between bg-white dark:bg-gray-900"
            >
              <div class="flex items-center gap-2">
                <h1 class="text-base font-bold text-gray-800 dark:text-gray-100">Notifications</h1>
                <span
                  v-if="notif_counts > 0"
                  class="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full"
                >
                  {{ notif_counts }} new
                </span>
              </div>
              <button
                v-if="notif_counts > 0"
                @click="markAllRead"
                class="text-xs text-primary hover:underline font-medium transition-all"
              >
                Mark all as read
              </button>
            </kt-card-header>

            <!-- Notification Body List -->
            <kt-card-body v-if="loading" class="p-10 flex justify-center bg-white dark:bg-gray-900">
              <span class="loading-spinner"></span>
            </kt-card-body>

            <kt-card-body v-else class="overflow-y-auto p-2 space-y-2 bg-white dark:bg-gray-900 max-h-[400px]">
              <!-- Empty State -->
              <div v-if="notifications.length === 0" class="py-12 flex flex-col items-center justify-center opacity-60">
                <img
                  src="/public/media/empty-state/no-data.svg"
                  alt="No Notifications"
                  class="w-20 mb-3 opacity-75 dark:invert"
                />
                <p class="text-sm font-semibold text-gray-700 dark:text-gray-300">All caught up!</p>
                <p class="text-xs text-gray-400 mt-0.5">You have no unread notifications.</p>
              </div>

              <!-- Notification Item Layout -->
              <div
                v-else
                v-for="value in notifications"
                :key="value.id"
                @click="goToNotif(value)"
                class="relative group p-3 rounded-xl cursor-pointer transition-all duration-200 flex items-start gap-3 border border-transparent"
                :class="[
                  !value.read_at
                    ? 'bg-blue-50/60 dark:bg-blue-950/20 hover:bg-blue-50 dark:hover:bg-blue-950/30'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/60',
                ]"
              >
                <!-- Dynamic Dot for Unread Status -->
                <span v-if="!value.read_at" class="absolute top-4 left-2 w-2 h-2 bg-primary rounded-full"></span>

                <!-- <pre>{{ notifications }}</pre> -->
                <!-- Content Area -->
                <div class="flex-grow pl-2">
                  <div class="flex items-center justify-between">
                    <h1
                      class="text-sm text-gray-800 dark:text-gray-200"
                      :class="[!value.read_at ? 'font-semibold' : 'font-medium']"
                    >
                      {{ value.title || "System Notification" }}
                    </h1>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2 leading-relaxed">
                    {{ value.message }}
                  </p>
                  <!-- Optional: Time Ago placeholder -->
                  <span class="text-[10px] text-gray-400 dark:text-gray-500 block mt-2 font-medium">
                    {{ value.created_at }}
                  </span>
                </div>
              </div>
            </kt-card-body>
          </kt-card>
        </transition>
      </div>

      <div class="relative" ref="dropdownRef">
        <div @click="toggleDropdown" class="flex items-center gap-4 cursor-pointer hover:opacity-80 transition-opacity">
          <kt-avatar :rounded-full="true" size="40" type="primary" class="shadow-sm">
            <img v-if="user?.avatar" :src="user.avatar" alt="User Profile" />
            <span v-else class="text-sm font-bold text-white">
              {{ user?.name ? user.name.charAt(0).toUpperCase() : "U" }}
            </span>
          </kt-avatar>
        </div>

        <transition name="fade-slide">
          <kt-card
            v-if="isDropdownOpen"
            class="absolute top-full right-0 mt-3 w-64 z-50 shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden"
          >
            <div class="p-4 bg-gray-50 dark:bg-gray-800/50 border-b dark:border-gray-700">
              <p class="text-sm font-bold truncate">{{ user?.name || "User Name" }}</p>
              <p class="text-xs text-gray-500 truncate">{{ user?.email || "user@example.com" }}</p>
            </div>

            <div class="p-2 space-y-1">
              <button
                @click="handleMenuAction('profile')"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors"
              >
                <kt-icon name="user" class="text-lg" />
                My Profile
              </button>

              <div class="border-t dark:border-gray-700 my-1"></div>

              <button
                @click="handleMenuAction('logout')"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <kt-icon name="exit-right" class="text-lg" />
                Sign Out
              </button>
            </div>
          </kt-card>
        </transition>
      </div>
    </div>
  </header>
</template>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s ease-out;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useNotificationStore } from "@/stores/notifications";
import useAction from "@/composables/useAction";
import { useRouter, useRoute } from "vue-router";
import { useDialog } from "metronics-vue";
import { toast } from "vue3-toastify";

const router = useRouter();
const route = useRoute();
const notif_store = useNotificationStore();
const authStore = useAuthStore();
const { showCustomConfirmDialog } = useDialog();

const notifications = computed(() => notif_store.notifications);
const notif_counts = computed(() => notif_store.count);
const user = authStore.user;

const isDropdownOpen = ref(false);
const isNotifDropdownOpen = ref(false);
const dropdownRef = ref(null);
const notifDropdownRef = ref(null);
let isInitialLoad = true;

const emit = defineEmits(["click:sidebar-toggler"]);

function handleMenuAction(action) {
  isDropdownOpen.value = false;
  if (action === "profile") {
    router.push(`/user-management/agent-profile/${user.id}`);
  } else if (action === "logout") {
    handleLogout();
  }
}

const toggleDropdown = () => {
  isNotifDropdownOpen.value = false;
  isDropdownOpen.value = !isDropdownOpen.value;
};

const toggleNotifDropdown = () => {
  isDropdownOpen.value = false;
  isNotifDropdownOpen.value = !isNotifDropdownOpen.value;
  if (isNotifDropdownOpen.value) {
    fetch_notif();
  }
};

const toggleSidebar = () => emit("click:sidebar-toggler");

const { invoke: fetch_notif, loading } = useAction(notif_store.fetch_all_notifications);
const { invoke: count_notif } = useAction(notif_store.count_all_notif);

// Triggered by "Mark all as read" button click
const markAllRead = async () => {
  await notif_store.mark_all_as_read(); // Ensure you map this method in your Pinia store to hit PUT /notifications/read-all
};

const goToNotif = async (notif) => {
  if (!notif.read_at) {
    await notif_store.mark_as_read(notif.id);
  }

  const routePath = notif.route;
  if (!routePath) return;

  let targetRoute = routePath.startsWith("/") ? routePath : `/${routePath}`;
  isNotifDropdownOpen.value = false;

  if (route.fullPath !== targetRoute) {
    router.push(targetRoute);
  }
};

const handleClickOutside = (e) => {
  if (
    dropdownRef.value &&
    !dropdownRef.value.contains(e.target) &&
    notifDropdownRef.value &&
    !notifDropdownRef.value.contains(e.target)
  ) {
    isDropdownOpen.value = false;
    isNotifDropdownOpen.value = false;
  }
};

async function handleLogout() {
  const confirm = await showCustomConfirmDialog({
    title: "Confirm Logout",
    subtitle: "Are you sure you want to log out? You will redirected to the EES login page.",
  });

  if (!confirm) return;

  try {
    await authStore.logout();
  } catch (error) {
    console.error("Logout failed:", error);
  }
}

watch(
  () => notif_store.count,
  async (newCount, oldCount) => {
    if (isInitialLoad) {
      isInitialLoad = false;
      return;
    }

    if (newCount > oldCount) {
      const delta = newCount - oldCount;

      try {
        await notif_store.fetch_all_notifications();
      } catch (err) {
        console.error("Could not fetch updated text lists:", err);
      }

      if (delta > 3) {
        toast.info(`🔥 You have ${delta} new notifications!`, {
          position: "top-right",
          autoClose: 5000,
          theme: "colored"
        });
        return;
      }

      const newItems = notifications.value.slice(0, delta);

      if (!newItems || newItems.length === 0) {
        toast.info(`You have a new notification!`, {
          position: "top-right",
          autoClose: 4000,
          theme: "colored"
        });
        return;
      }

      newItems.reverse().forEach((notif, index) => {
        setTimeout(() => {
          toast.info(`New: ${notif.title || "System Update"}`, {
            position: "top-right",
            autoClose: 4000,
            theme: "colored"
          });
        }, index * 250);
      });
    }
  },
  { immediate: true }
);

onMounted(() => {
  count_notif();
  notif_store.startPolling(30000);
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  notif_store.stopPolling();
  document.removeEventListener("click", handleClickOutside);
});
</script>
